import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { PlaylistApi } from 'app/modules/dashboard/api/playlist.api';
import { BehaviorSubject, Observable, Subscription, map } from 'rxjs';
import { SkeletonModule } from 'primeng/skeleton';
import { RippleModule } from 'primeng/ripple';
import { PlaylistElementComponent } from './playlist-element/playlist-element.component';
import { NgIf, NgStyle, NgFor, AsyncPipe } from '@angular/common';
import { ApiHttpResponse, VideoSection } from '@kinomap-dev/ng-ui-lib';
import { ToasterService } from 'app/services/toaster.service';

@Component({
  selector: 'app-watch-playlist-modal',
  templateUrl: './watchPlaylistModal.component.html',
  styleUrls: ['./watchPlaylistModal.component.scss'],
  standalone: true,
  imports: [
    TranslateModule,
    NgIf,
    NgStyle,
    NgFor,
    PlaylistElementComponent,
    RippleModule,
    SkeletonModule,
    AsyncPipe,
  ],
})
export class WatchPlaylistModal implements OnInit, OnDestroy {
  @Input() scrollContainerSelector: string;
  @Input() videoId: number;
  @Input() type: 'watch' | 'scenic' | 'coaching';

  @Output() closeModal: EventEmitter<any>;
  @Output() newPlaylist: EventEmitter<any>;

  subscription: Subscription[] = [];

  page: number = 1;
  lastPage: number = 1;
  playlists: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  postProgress: boolean = false;
  playlistLoading$: BehaviorSubject<boolean>;
  selectedPlaylistId: number = null;

  constructor(
    private playlistApi: PlaylistApi,
    private toasterService: ToasterService,
    public translate: TranslateService
  ) {
    this.closeModal = new EventEmitter<void>(null);
    this.newPlaylist = new EventEmitter<void>(null);
    this.playlistLoading$ = new BehaviorSubject<boolean>(true);
  }

  createNewPlaylist() {
    this.newPlaylist.emit();
  }

  getPlaylists(page: number) {
    this.playlistLoading$.next(true);
    if (page <= this.lastPage) {
      this.subscription = [
        ...this.subscription,
        this.playlistApi.getPlaylists(page, 15).subscribe((res) => {
          const existingPlaylists = this.playlists.getValue();
          const updatedPlaylists = [...existingPlaylists, ...res.data];
          this.playlists.next(updatedPlaylists);
          this.lastPage = res.meta.last_page;
          this.playlistLoading$.next(false);
        }),
      ];
    }
  }

  morePlaylists() {
    this.page += 1;
    this.getPlaylists(this.page);
  }

  elementClick(event: number) {
    this.selectedPlaylistId = event;
  }

  addToPlaylist(playlistId: number) {
    let videoType: string;
    switch (this.type) {
      case 'watch':
      case 'scenic':
        videoType = 'scenic';
        break;
      case 'coaching':
        videoType = 'coaching';
        break;
    }

    if (!this.postProgress) {
      this.subscription = [
        ...this.subscription,
        this.checkPlaylistContainVideo(
          this.getVideoFromPlaylist(playlistId),
          this.videoId
        ).subscribe((containVideo) => {
          this.postProgress = true;
          if (containVideo) {
            this.toasterService.warning(
              null,
              this.translate.instant(
                'watch.playlist_modal.toast.already_in_playlist'
              )
            );
            return (this.postProgress = false);
          }
          this.playlistApi
            .addVideoToPlaylist(playlistId, this.videoId, videoType)
            .subscribe((res: any) => {
              if (res.message === 'OK') {
                this.toasterService.success(
                  null,
                  this.translate.instant(
                    'watch.playlist_modal.toast.add_in_playlist_success'
                  )
                );
              } else {
                this.toasterService.error(
                  null,
                  this.translate.instant(
                    'watch.playlist_modal.toast.add_in_playlist_error'
                  )
                );
              }
              this.closePlaylistModal();
              this.postProgress = false;
            });
        }),
      ];
    }
  }

  getVideoFromPlaylist(playlistId: number): Observable<VideoSection[]> {
    return this.playlistApi
      .getVideoFromPlaylist(playlistId)
      .pipe(map((res: ApiHttpResponse<VideoSection[]>) => res.data));
  }

  checkPlaylistContainVideo(
    playlist: Observable<VideoSection[]>,
    videoId: number
  ): Observable<boolean> {
    return playlist.pipe(
      map((videos) => videos.some((video) => video.id === videoId))
    );
  }

  closePlaylistModal() {
    this.closeModal.emit();
  }

  ngOnInit(): void {
    this.getPlaylists(this.page);
  }

  ngOnDestroy(): void {
    this.subscription.forEach((res) => res.unsubscribe());
  }
}
